"use client";

import React from "react";
import { Share2, Linkedin, Twitter, Facebook, Instagram, Youtube, Github, ExternalLink } from "lucide-react";
import { CompanyInfoCard } from "./CompanyInfoCard";
import { Button } from "@/components/ui/button";
import type { CompanyEntity } from "@/lib/types/company";

interface CompanySocialLinksProps {
  company: CompanyEntity;
}

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  linkedin: <Linkedin className="h-4 w-4" />,
  twitter: <Twitter className="h-4 w-4" />,
  facebook: <Facebook className="h-4 w-4" />,
  instagram: <Instagram className="h-4 w-4" />,
  youtube: <Youtube className="h-4 w-4" />,
  github: <Github className="h-4 w-4" />,
};

export function CompanySocialLinks({ company }: CompanySocialLinksProps) {
  if (!company.social_links) return null;

  let socialLinks: Record<string, string> = {};

  try {
    if (typeof company.social_links === "string") {
      socialLinks = JSON.parse(company.social_links);
    } else if (typeof company.social_links === "object" && company.social_links !== null) {
      socialLinks = company.social_links as Record<string, string>;
    }
  } catch (e) {
    console.error("Failed to parse social links:", e);
  }

  const links = Object.entries(socialLinks).filter(([key, value]) => 
    typeof value === "string" && value.trim()
  );

  if (links.length === 0) return null;

  return (
    <CompanyInfoCard title="Social Media" icon={Share2}>
      <div className="space-y-2">
        {links.map(([platform, url]) => {
          const icon = SOCIAL_ICONS[platform.toLowerCase()] || <ExternalLink className="h-4 w-4" />;
          const displayName = platform.charAt(0).toUpperCase() + platform.slice(1);

          return (
            <Button
              key={platform}
              variant="outline"
              size="sm"
              asChild
              className="w-full justify-start gap-2"
            >
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                {icon}
                <span className="flex-1 text-left">{displayName}</span>
                <ExternalLink className="h-3 w-3 opacity-50" />
              </a>
            </Button>
          );
        })}
      </div>
    </CompanyInfoCard>
  );
}
